import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Share2, Download, Check } from "lucide-react";
import { copyToClipboard, canWebShare, nativeShare } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

/** Lien court + QR (généré côté client, aucun service externe) + tracking par source via ?s=. */
export function useShareLink(slug, code) {
  const [c, setC] = useState(code || null);
  useEffect(() => {
    if (code || !slug) return;
    axios.get(`${API}/public/simulator/share`, { params: { slug } }).then((r) => setC(r.data.code)).catch(() => setC(null));
  }, [slug, code]);
  const base = c ? `${ORIGIN}/l/${c}` : null;
  return { code: c, base, url: (s) => (base ? `${base}?s=${s}` : null) };
}

export function ShareActions({ share, title, compact, testid = "share" }) {
  const [copied, setCopied] = useState(false);
  if (!share.base) return null;
  const copy = async () => { const ok = await copyToClipboard(share.url("copy")); setCopied(ok); setTimeout(() => setCopied(false), 2000); };
  const native = async () => {
    const r = await nativeShare({ title, text: title, url: share.url("share_native") });
    if (r === "unsupported") copy();
  };
  const canNative = canWebShare({ url: share.url("share_native") });
  const cls = `inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors ${compact ? "h-10 px-3 text-[13px]" : "h-11 px-4 text-sm"}`;
  return (
    <div className={`grid gap-2 ${canNative ? "grid-cols-2" : "grid-cols-1"}`}>
      {canNative && (
        <button type="button" onClick={native} className={`${cls} text-white bg-[color:var(--cf-primary)] hover:bg-[color:var(--cf-primary-hover)]`} data-testid={`${testid}-native-btn`}>
          <Share2 size={16} /> Partager
        </button>
      )}
      <button type="button" onClick={copy} className={`${cls} bg-white border border-slate-200 text-slate-800 hover:bg-slate-50`} data-testid={`${testid}-copy-btn`}>
        {copied ? <Check size={16} className="text-[color:var(--cf-primary)]" /> : <Copy size={16} />} {copied ? "Lien copié" : "Copier le lien"}
      </button>
    </div>
  );
}

export function ShareQR({ share, size = 148, fileName = "simulateur-qr", showDownload = true, testid = "share-qr" }) {
  const ref = useRef(null);
  if (!share.base) return null;
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = `${fileName}.png`; a.href = canvas.toDataURL("image/png"); a.click();
  };
  return (
    <div className="flex flex-col items-center gap-2" data-testid={testid}>
      <div ref={ref} className="rounded-xl border border-slate-200 bg-white p-3">
        <QRCodeCanvas value={share.url("qr")} size={size} level="M" marginSize={1} fgColor="#171717" bgColor="#FFFFFF" />
      </div>
      {showDownload && (
        <button type="button" onClick={download} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600 hover:text-slate-900" data-testid={`${testid}-download-btn`}>
          <Download size={14} /> Télécharger le QR (PNG)
        </button>
      )}
    </div>
  );
}

/** Bloc de fin de parcours : « Partagez ce simulateur ». */
export function SimShareBlock({ slug, config }) {
  const share = useShareLink(slug);
  const name = (config?.display_name || "votre courtier").split(" ")[0];
  if (!share.base) return null;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sim-share-block">
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Partagez ce simulateur</div>
      <div className="font-extrabold text-slate-900 text-[16px] leading-tight mt-1">Un proche a un projet immobilier ?</div>
      <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">Envoyez-lui ce lien : il obtiendra son premier avis en 2 minutes et pourra échanger avec {name}.</p>
      <div className="mt-4 flex items-start gap-4">
        <ShareQR share={share} size={96} showDownload={false} testid="sim-share-qr" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[12.5px] text-slate-700 break-all rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3" data-testid="sim-share-url">{share.base.replace(/^https?:\/\//, "")}</div>
          <ShareActions share={share} compact title={`Simulateur de financement immobilier — ${name}`} testid="sim-share" />
        </div>
      </div>
    </div>
  );
}
